// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WidgetTitle from "@bg/App/Components/WidgetTitle";
import loadMonthlyDeals from "@bg/App/Handlers/MonthlyDeals/LoadMonthlyDeals";
import { CustomAppState } from "@bg/App/Store/Reducers";
import MonthlyDealItem from "@bg/App/Widgets/MonthlyDeals/MonthlyDealItem";
import { styledSlider } from "@bg/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Scrollbar, A11y]);

const enum fields {
    title = "title",
    viewAllText = "viewAllText",
    viewAllLink = "viewAllLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.viewAllText]: string;
        [fields.viewAllLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        monthlyDeals: state.xMonthlyDelas.monthlyDeals,
        isLoading: state.xMonthlyDelas.isLoading,
        isAuthenticated: state.context.session.isAuthenticated,
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

const mapDispatchToProps = {
    loadMonthlyDeals,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface MonthlyDealsStyles {}

export const styles: MonthlyDealsStyles = {};

export const popularCategoriesContainer = css`
    ${styledSlider()}
    .swiper-container {
        width: 100% !important;
        padding: 15px;
    }

    .swiper-slide {
        display: flex;
        justify-content: center;
        @media screen and (max-width: 1329px) {
            min-width: 0;
        }
        > div:first-child {
            height: 100%;

            @media (max-width: 768px) {
                max-width: unset;
            }
        }
    }
`;

const MAX_SLIDES = 2;

const MonthlyDeals = ({
    fields,
    loadMonthlyDeals,
    isLoading,
    monthlyDeals,
    isAuthenticated,
    customSettings,
}: Props) => {
    const [showSpinner, setShowSpinner] = useState(true);

    const url = useGetLink(fields.viewAllLink)?.url;

    useEffect(() => {
        if (
            (typeof window === "undefined" || !window.localStorage.getItem("postalCodeID")?.length) &&
            !isAuthenticated &&
            customSettings &&
            customSettings.postalCodeLocationFinder.toLowerCase() === "true"
        ) {
            setShowSpinner(false);
            return;
        }
        loadMonthlyDeals({});
        // eslint-disable-next-line global-require
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };

        getStyles().then(result => {
            setShowSpinner(false);
        });
    }, []);

    if (isLoading || showSpinner) {
        return (
            <StyledWrapper
                css={css`
                    height: 100px;
                    width: 100%;
                    padding: 10px 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!monthlyDeals || monthlyDeals.length === 0) {
        return null;
    }

    return (
        <>
            <WidgetTitle icon="MonthlyDeals" title={fields.title} viewAllText={fields.viewAllText} viewAllLink={url} />
            <StyledWrapper
                css={css`
                    > div {
                        display: flex;
                        justify-content: center;
                    }
                    @media print {
                        display: none;
                    }
                `}
            >
                <StyledWrapper className="carousel-monthly-deals-container" css={popularCategoriesContainer}>
                    <Swiper
                        spaceBetween={30}
                        breakpoints={{
                            0: {
                                slidesPerView: 1,
                            },
                            1023: {
                                slidesPerView: MAX_SLIDES,
                            },
                        }}
                        className="swiper-carousel"
                        navigation={{
                            nextEl: `.swiper-button-next.swiper-button-next${monthlyDeals[0].productId}`,
                            prevEl: `.swiper-button-prev.swiper-button-prev${monthlyDeals[0].productId}`,
                        }}
                        loop={monthlyDeals.length > MAX_SLIDES}
                    >
                        {monthlyDeals.map(
                            ({
                                productId,
                                availableDateDisplay,
                                brandImage,
                                productDescription,
                                productImage,
                                productTitle,
                                pageUrl,
                                product,
                            }) => (
                                <SwiperSlide key={`popular_category_${productId}`}>
                                    <MonthlyDealItem
                                        productId={productId}
                                        availableDateDisplay={availableDateDisplay}
                                        brandImage={brandImage}
                                        productDescription={productDescription}
                                        productImage={productImage}
                                        productTitle={productTitle}
                                        pageUrl={pageUrl}
                                        product={product}
                                    />
                                </SwiperSlide>
                            ),
                        )}
                    </Swiper>
                    <StyledWrapper className="swiper-navigation">
                        <div className={`swiper-button-prev swiper-button-prev${monthlyDeals[0].productId}`}>
                            <ChevronLeft />
                        </div>
                        <div className={`swiper-button-next swiper-button-next${monthlyDeals[0].productId}`}>
                            <ChevronRight />
                        </div>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Monthly Deals",
    fieldDefinitions: [
        {
            displayName: "Section Title",
            name: "title",
            editorTemplate: "TextField",
            defaultValue: "Monthly Deals",
            fieldType: "Translatable",
        },
        {
            displayName: "Shop Clearance Text",
            name: "viewAllText",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
        },
        {
            displayName: "Shop Clearance Url",
            name: "viewAllLink",
            editorTemplate: "LinkField",
            defaultValue: {
                value: "",
                type: "Url",
            },
            fieldType: "General",
        },
    ],
    allowedContexts: ["HomePage"],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MonthlyDeals),
    definition,
};

export default widgetModule;
